/**
 * Platform Users Tab
 * Cross-tenant user management for super_admin users
 * Allows viewing all users across all tenants and enabling/disabling for TOS violations
 */

import { useState, useCallback, useMemo } from "react";
import {
  usePlatformUsers,
  usePlatformUserStats,
  useUpdateUserStatus,
} from "@hooks/usePlatformAdmin";
import {
  DataTable,
  type Column,
  Modal,
  SearchInput,
  StatusBadge,
} from "@components/admin";
import { Button } from "@components/common";
import { StudioManagedUsersBanner } from "@components/admin/StudioManagedUsersBanner";
import { formatDate } from "@utils/format";
import { isStudioSso } from "@/config/studio";
import type { PlatformUser, PlatformUserFilters } from "@/types/platformAdmin";
import { showToast } from "@stores/uiStore";

type SortDirection = "asc" | "desc";

// Default tenant slug for individual users (matches backend DEFAULT_TENANT_SLUG)
const DEFAULT_TENANT_SLUG = "individuals";

export function PlatformUsersTab() {
  // studio_sso: user management lives in Studio, not Hub.
  if (isStudioSso()) {
    return <StudioManagedUsersBanner />;
  }
  return <HubJwtPlatformUsersTab />;
}

function HubJwtPlatformUsersTab() {
  // Filters & pagination
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "disabled"
  >("all");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [tenantFilter, setTenantFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>("email");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const pageSize = 20;

  // Modals
  const [userToUpdate, setUserToUpdate] = useState<PlatformUser | null>(null);
  const [disableReason, setDisableReason] = useState("");

  // Build filters object
  const filters: PlatformUserFilters = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter === "all" ? undefined : statusFilter,
      role: roleFilter || undefined,
      tenantId: tenantFilter || undefined,
      page,
      pageSize,
    }),
    [search, statusFilter, roleFilter, tenantFilter, page, pageSize],
  );

  // Queries & mutations
  const { data: usersData, isLoading, refetch } = usePlatformUsers(filters);

  const { data: stats } = usePlatformUserStats();
  const updateStatus = useUpdateUserStatus();

  // Get unique tenants from stats for filter dropdown
  const tenantOptions = useMemo(() => {
    if (!stats?.byTenant) return [];
    return Object.entries(stats.byTenant).map(([slug, count]) => ({
      slug,
      count,
    }));
  }, [stats]);

  // Get unique roles from stats for filter dropdown
  const roleOptions = useMemo(() => {
    if (!stats?.byRole) return [];
    return Object.entries(stats.byRole).map(([role, count]) => ({
      role,
      count,
    }));
  }, [stats]);

  // Sort users client-side
  const users = usersData?.users;
  const sortedUsers = useMemo(() => {
    if (!users) return [];
    return [...users].sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortColumn];
      const bVal = (b as unknown as Record<string, unknown>)[sortColumn];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [users, sortColumn, sortDirection]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn],
  );

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!userToUpdate) return;

    try {
      const newStatus = !userToUpdate.isActive;
      await updateStatus.mutateAsync({
        userId: userToUpdate.id,
        data: {
          isActive: newStatus,
          reason: disableReason || undefined,
        },
      });

      showToast(
        `User ${userToUpdate.email} has been ${newStatus ? "enabled" : "disabled"}`,
        "success",
      );
      setUserToUpdate(null);
      setDisableReason("");
    } catch (error) {
      showToast(
        `Failed to update user status: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error",
      );
    }
  };

  // Table columns
  const columns: Column<PlatformUser>[] = useMemo(
    () => [
      {
        key: "email",
        header: "User",
        sortable: true,
        render: (user) => (
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              {user.email}
            </div>
            {user.fullName && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {user.fullName}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "tenantName",
        header: "Organization",
        sortable: true,
        render: (user) => (
          <div className="flex items-center gap-2">
            <div>
              <div className="text-gray-900 dark:text-white">
                {user.tenantName}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {user.tenantSlug}
              </div>
            </div>
            {user.tenantSlug === DEFAULT_TENANT_SLUG && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                Individual
              </span>
            )}
          </div>
        ),
      },
      {
        key: "role",
        header: "Role",
        sortable: true,
        width: "120px",
        render: (user) => (
          <span className="text-sm capitalize text-gray-700 dark:text-gray-300">
            {user.role.replace("_", " ")}
          </span>
        ),
      },
      {
        key: "isActive",
        header: "Status",
        sortable: true,
        width: "100px",
        render: (user) => (
          <StatusBadge
            label={user.isActive ? "Active" : "Disabled"}
            variant={user.isActive ? "success" : "error"}
            dot
          />
        ),
      },
      {
        key: "createdAt",
        header: "Created",
        sortable: true,
        width: "120px",
        render: (user) => (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {formatDate(user.createdAt)}
          </span>
        ),
      },
      {
        key: "actions",
        header: "",
        width: "100px",
        render: (user) => (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setUserToUpdate(user)}
              className={`p-1 transition-colors ${
                user.isActive
                  ? "text-gray-400 hover:text-red-500"
                  : "text-gray-400 hover:text-green-500"
              }`}
              title={user.isActive ? "Disable user" : "Enable user"}
            >
              {user.isActive ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      {/* Header with Stats */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Platform User Management
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage users across all organizations for TOS/policy enforcement
          </p>
        </div>
        {stats && (
          <div className="flex gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalUsers}
              </div>
              <div className="text-gray-500 dark:text-gray-400">
                Total Users
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.activeUsers}
              </div>
              <div className="text-gray-500 dark:text-gray-400">Active</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {stats.disabledUsers}
              </div>
              <div className="text-gray-500 dark:text-gray-400">Disabled</div>
            </div>
            <div className="text-center border-l border-gray-200 dark:border-gray-700 pl-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {stats.byTenant[DEFAULT_TENANT_SLUG] || 0}
              </div>
              <div className="text-gray-500 dark:text-gray-400">
                Individuals
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search by email or name..."
          className="w-64"
        />

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as "all" | "active" | "disabled");
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="disabled">Disabled Only</option>
        </select>

        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option value="">All Roles</option>
          {roleOptions.map(({ role, count }) => (
            <option key={role} value={role}>
              {role.replace("_", " ")} ({count})
            </option>
          ))}
        </select>

        <select
          value={tenantFilter}
          onChange={(e) => {
            setTenantFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option value="">All Organizations</option>
          {/* Show Individuals first with special label */}
          {tenantOptions.some((t) => t.slug === DEFAULT_TENANT_SLUG) && (
            <option value={DEFAULT_TENANT_SLUG}>
              ★ Individuals ({stats?.byTenant[DEFAULT_TENANT_SLUG] || 0})
            </option>
          )}
          {/* Then show other organizations */}
          {tenantOptions
            .filter(({ slug }) => slug !== DEFAULT_TENANT_SLUG)
            .map(({ slug, count }) => (
              <option key={slug} value={slug}>
                {slug} ({count})
              </option>
            ))}
        </select>

        <button
          onClick={() => refetch()}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Refresh"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      {/* Data Table */}
      <DataTable
        data={sortedUsers}
        columns={columns}
        keyExtractor={(user) => user.id}
        isLoading={isLoading}
        emptyMessage="No users found"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      {/* Pagination */}
      {usersData && usersData.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(page - 1) * pageSize + 1} to{" "}
            {Math.min(page * pageSize, usersData.total)} of {usersData.total}{" "}
            users
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
              Page {page} of {usersData.totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setPage((p) => Math.min(usersData.totalPages, p + 1))
              }
              disabled={page === usersData.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Enable/Disable User Modal */}
      {userToUpdate && (
        <Modal
          isOpen={!!userToUpdate}
          onClose={() => {
            setUserToUpdate(null);
            setDisableReason("");
          }}
          title={userToUpdate.isActive ? "Disable User" : "Enable User"}
          description={userToUpdate.email}
          size="md"
          footer={
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setUserToUpdate(null);
                  setDisableReason("");
                }}
                disabled={updateStatus.isPending}
              >
                Cancel
              </Button>
              <Button
                variant={userToUpdate.isActive ? "danger" : "primary"}
                onClick={handleStatusUpdate}
                isLoading={updateStatus.isPending}
              >
                {userToUpdate.isActive ? "Disable User" : "Enable User"}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">User:</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {userToUpdate.email}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">
                  Organization:
                </span>
                <span className="text-gray-900 dark:text-white">
                  {userToUpdate.tenantName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Role:</span>
                <span className="text-gray-900 dark:text-white capitalize">
                  {userToUpdate.role.replace("_", " ")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">
                  Current Status:
                </span>
                <StatusBadge
                  label={userToUpdate.isActive ? "Active" : "Disabled"}
                  variant={userToUpdate.isActive ? "success" : "error"}
                  dot
                />
              </div>
            </div>

            {userToUpdate.isActive && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason for Disabling (optional)
                </label>
                <textarea
                  value={disableReason}
                  onChange={(e) => setDisableReason(e.target.value)}
                  placeholder="e.g., TOS violation, policy breach, etc."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This reason will be logged for audit purposes.
                </p>
              </div>
            )}

            {userToUpdate.role === "super_admin" && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  <strong>Warning:</strong> You are about to{" "}
                  {userToUpdate.isActive ? "disable" : "enable"} a super_admin
                  user. This action will affect their ability to manage the
                  platform.
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
